import React from "react";
import { formatCardAddress } from "@/lib/formatters";

/**
 * BROWSER PREVIEW ONLY — not production print artwork.
 * Each template is an INDEPENDENT graphic design with its own palette; they are
 * deliberately not extensions of the website brand colours.
 * Every renderer adapts when there is no logo, supports an optional business
 * tagline (Bidang Bisnes / Moto) and skips empty optional fields entirely.
 */

const Logo = ({ logo, cls = "", invert = false }) =>
  logo ? (
    <img
      src={logo}
      alt=""
      className={`object-contain ${invert ? "brightness-0 invert" : ""} ${cls}`}
      data-testid="card-logo-image"
    />
  ) : null;

const Lines = ({ items, className = "" }) =>
  items.length ? (
    <div className={`space-y-[0.12em] ${className}`}>
      {items.map((l, i) => (
        <div key={`${l}-${i}`} className="truncate">
          {l}
        </div>
      ))}
    </div>
  ) : null;

const clean = (v) => (typeof v === "string" ? v.trim() : "");

const contactLines = (b) =>
  [
    clean(b.telefon) && `T ${clean(b.telefon)}`,
    clean(b.whatsapp) && `WA ${clean(b.whatsapp)}`,
    clean(b.email),
    clean(b.website),
  ].filter(Boolean);

const monogram = (biz) =>
  clean(biz)
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join("") || "B";

/** Optional tagline. Never falls back to state/address data. */
const Tagline = ({ text, className }) =>
  text ? <div className={`truncate ${className}`}>{text}</div> : null;

export const CardTemplate = ({ styleKey, side = "front", data, logo }) => {
  const b = data.business || {};
  const hasLogo = Boolean(logo);
  const name = clean(b.namaPenuh) || "Nama Penuh Anda";
  const biz = clean(b.namaPerniagaan) || "Nama Perniagaan";
  const role = clean(b.jawatan);
  const tag = clean(b.bidangBisnes);
  const addr = formatCardAddress(data.businessAddress || {});
  const contacts = contactLines(b);

  const shared = { b, name, biz, role, tag, addr, contacts, hasLogo, logo, side };
  const Renderer = RENDERERS[styleKey];

  // Fail loudly instead of silently substituting another design.
  if (!Renderer) {
    return (
      <div
        className="h-full w-full grid place-items-center bg-[#fdf3f3] border border-dashed border-[#d98a8e] p-4 text-center"
        data-testid="card-template-unknown"
        role="alert"
      >
        <p className="text-xs font-semibold text-[#b4242b] leading-snug">
          Design tidak dapat dipaparkan.
          <br />
          Sila pilih design lain.
        </p>
      </div>
    );
  }

  return (
    <div
      className="w-full h-full overflow-hidden select-none"
      style={{ containerType: "inline-size" }}
      data-testid={`card-face-${side}`}
    >
      <div className="w-full h-full text-[3.4cqw] leading-tight">
        <Renderer {...shared} />
      </div>
    </div>
  );
};

/* ===================== BC01 — Minimal · charcoal & white ===================== */
const Minimal = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-white text-[#17181A] flex">
      <div className="w-[0.7em] bg-[#17181A]" />
      <div className="flex-1 p-[1.3em] flex flex-col justify-between">
        <div className="flex items-start justify-between gap-[0.6em]">
          <div className="min-w-0">
            <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.45em]" : "text-[1.85em]"}`}>
              {biz}
            </div>
            <Tagline
              text={tag}
              className="mt-[0.25em] text-[0.82em] uppercase tracking-[0.2em] text-[#8A8D93]"
            />
          </div>
          {hasLogo && <Logo logo={logo} cls="h-[2.8em] w-[2.8em] shrink-0" />}
        </div>
        <div>
          <div className="text-[1.1em] font-semibold">{name}</div>
          {role && <div className="text-[0.85em] text-[#6C7075]">{role}</div>}
          <Lines items={contacts} className="mt-[0.3em] text-[0.85em] text-[#54585D]" />
        </div>
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#17181A] text-white p-[1.4em] flex flex-col justify-center">
      {hasLogo && <Logo logo={logo} invert cls="h-[2.6em] w-[2.6em] mb-[0.5em]" />}
      <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.4em]" : "text-[1.85em]"}`}>
        {biz}
      </div>
      <Tagline text={tag} className="mt-[0.2em] text-[0.82em] uppercase tracking-[0.2em] text-white/60" />
      <div className="mt-[0.35em] h-[0.14em] w-[3em] bg-white/70" />
      <Lines items={addr} className="mt-[0.45em] text-[0.85em] text-white/75" />
    </div>
  );

/* ===================== BC02 — Corporate · navy blue ===================== */
const Corporate = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full flex bg-white">
      <div className="w-[31%] bg-gradient-to-b from-[#1B3D74] to-[#102647] text-white grid place-items-center p-[0.7em]">
        {hasLogo ? (
          <Logo logo={logo} invert cls="max-h-[65%] max-w-[80%]" />
        ) : (
          <span className="text-[2.3em] font-extrabold tracking-tight">{monogram(biz)}</span>
        )}
      </div>
      <div className="flex-1 flex flex-col justify-center pl-[1.2em] pr-[1em] text-[#131A24] min-w-0">
        <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.35em]" : "text-[1.6em]"}`}>
          {biz}
        </div>
        <Tagline text={tag} className="text-[0.82em] text-[#1B3D74]" />
        <div className="mt-[0.3em] h-[0.12em] w-[2.2em] bg-[#B98A2E]" />
        <div className="mt-[0.4em] text-[1.05em] font-semibold">{name}</div>
        {role && (
          <div className="text-[0.85em] uppercase tracking-[0.16em] text-[#6B7482]">{role}</div>
        )}
        <Lines items={contacts} className="mt-[0.4em] text-[0.85em] text-[#4C5665]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-gradient-to-br from-[#1B3D74] to-[#0E1E38] text-white p-[1.4em] flex flex-col justify-center">
      <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.3em]" : "text-[1.75em]"}`}>
        {biz}
      </div>
      <Tagline text={tag} className="text-[0.82em] text-[#C6D4EA]" />
      <Lines items={addr} className="mt-[0.45em] text-[0.85em] text-[#C6D4EA]/85" />
    </div>
  );

/* ===================== BC03 — Modern · ink & cyan ===================== */
const ModernDark = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-[#0E1114] text-white p-[1.3em] flex flex-col justify-between relative overflow-hidden">
      <div className="absolute -top-[3em] -right-[3em] h-[8em] w-[8em] rounded-full bg-[#38BDF8]/15 blur-[0.6em]" />
      <div className="relative flex items-start justify-between gap-[0.6em]">
        {hasLogo ? (
          <Logo logo={logo} invert cls="h-[2.6em] w-[2.6em]" />
        ) : (
          <span className="text-[0.8em] uppercase tracking-[0.34em] text-[#7DD3FC]">
            {role || "Perniagaan"}
          </span>
        )}
        <span className="h-[0.16em] w-[2.8em] mt-[0.7em] bg-gradient-to-r from-[#38BDF8] to-transparent" />
      </div>
      <div className="relative text-right min-w-0">
        <div
          className={`font-extrabold tracking-tight text-[#7DD3FC] ${
            hasLogo ? "text-[1.5em]" : "text-[2em]"
          }`}
        >
          {biz}
        </div>
        <Tagline text={tag} className="text-[0.82em] text-[#94A3B8]" />
        <div className="mt-[0.15em] text-[1.05em] font-medium">{name}</div>
        {hasLogo && role && <div className="text-[0.85em] text-[#94A3B8]">{role}</div>}
        <Lines items={contacts} className="mt-[0.3em] text-[0.82em] text-[#94A3B8]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-gradient-to-br from-[#38BDF8] to-[#0B4A6F] text-white p-[1.4em] flex flex-col justify-center">
      {hasLogo && <Logo logo={logo} invert cls="h-[2.5em] w-[2.5em] mb-[0.45em]" />}
      <div className={`font-extrabold tracking-tight ${hasLogo ? "text-[1.4em]" : "text-[1.9em]"}`}>
        {biz}
      </div>
      <Tagline text={tag} className="text-[0.82em] text-white/80" />
      <Lines items={addr} className="mt-[0.4em] text-[0.85em] text-white/85" />
    </div>
  );

/* ===================== BC04 — Elegant · cream & burgundy ===================== */
const Elegant = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-[#FBF6EF] text-[#2A1D1F] p-[1.4em] flex flex-col items-center justify-center text-center">
      {hasLogo && <Logo logo={logo} cls="h-[2.5em] w-[2.5em] mb-[0.5em]" />}
      <div
        className={`font-serif uppercase tracking-[0.2em] ${
          hasLogo ? "text-[1.25em]" : "text-[1.7em]"
        }`}
      >
        {biz}
      </div>
      <Tagline
        text={tag}
        className="mt-[0.2em] max-w-full text-[0.82em] italic text-[#7C2B39]"
      />
      <div className="my-[0.5em] flex items-center gap-[0.4em]">
        <span className="h-px w-[2em] bg-[#7C2B39]/60" />
        <span className="h-[0.28em] w-[0.28em] rotate-45 bg-[#7C2B39]" />
        <span className="h-px w-[2em] bg-[#7C2B39]/60" />
      </div>
      <div className="text-[1.05em] font-semibold">{name}</div>
      {role && (
        <div className="text-[0.8em] uppercase tracking-[0.24em] text-[#2A1D1F]/55">{role}</div>
      )}
      <Lines items={contacts} className="mt-[0.35em] text-[0.82em] text-[#2A1D1F]/70" />
    </div>
  ) : (
    <div className="h-full w-full bg-[#5E1F2A] text-[#FBF6EF] p-[1.4em] flex flex-col items-center justify-center text-center">
      <div
        className={`font-serif uppercase tracking-[0.24em] ${
          hasLogo ? "text-[1.2em]" : "text-[1.6em]"
        }`}
      >
        {biz}
      </div>
      <Tagline text={tag} className="mt-[0.15em] text-[0.8em] italic text-[#E8C9A0]" />
      <span className="my-[0.45em] h-px w-[3em] bg-[#E8C9A0]" />
      <Lines items={addr} className="text-[0.82em] opacity-80" />
    </div>
  );

/* ===================== BC05 — Professional · forest green ===================== */
const TwoTone = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full flex flex-col bg-white">
      <div className="h-[46%] bg-gradient-to-r from-[#0F3F2F] to-[#17624A] text-white flex items-center px-[1.3em] gap-[0.7em]">
        {hasLogo && <Logo logo={logo} invert cls="h-[2.3em] w-[2.3em] shrink-0" />}
        <div className="min-w-0">
          <div
            className={`font-extrabold tracking-tight truncate ${
              hasLogo ? "text-[1.2em]" : "text-[1.6em]"
            }`}
          >
            {biz}
          </div>
          <Tagline text={tag} className="text-[0.78em] text-emerald-100/90" />
        </div>
      </div>
      <div className="h-[0.2em] bg-[#E3B84B]" />
      <div className="flex-1 px-[1.3em] flex flex-col justify-center text-[#14201b]">
        <div className="text-[1.05em] font-semibold">{name}</div>
        {role && <div className="text-[0.85em] text-[#5A6862]">{role}</div>}
        <Lines items={contacts} className="mt-[0.28em] text-[0.83em] text-[#4a5a53]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#F6F7F5] text-[#14201b] flex flex-col">
      <div className="h-[0.55em] bg-gradient-to-r from-[#0F3F2F] to-[#E3B84B]" />
      <div className="flex-1 p-[1.4em] flex flex-col justify-center">
        <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.3em]" : "text-[1.7em]"}`}>
          {biz}
        </div>
        <Tagline text={tag} className="text-[0.82em] text-[#17624A]" />
        <Lines items={addr} className="mt-[0.4em] text-[0.85em] text-[#4a5a53]" />
      </div>
    </div>
  );

/* ===================== BC06 — Creative · blue ===================== */
const Creative = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-gradient-to-br from-[#3B82F6] via-[#2563EB] to-[#1E2A78] text-white p-[1.2em] flex flex-col justify-between relative overflow-hidden">
      <div className="absolute -bottom-[3em] -left-[2em] h-[7em] w-[7em] rounded-full bg-[#F472B6]/25" />
      <div className="absolute -top-[2.5em] right-[1em] h-[5em] w-[5em] rounded-full bg-[#93C5FD]/25" />
      <div className="relative flex items-start justify-between gap-[0.6em]">
        <div className="min-w-0">
          <div
            className={`font-extrabold tracking-tight ${hasLogo ? "text-[1.4em]" : "text-[1.9em]"}`}
          >
            {biz}
          </div>
          <Tagline text={tag} className="text-[0.82em] text-[#DBEAFE]" />
        </div>
        {hasLogo && (
          <Logo logo={logo} cls="h-[2.4em] w-[2.4em] shrink-0 rounded-[0.3em] bg-white/90 p-[0.2em]" />
        )}
      </div>
      <div className="relative rounded-[0.55em] bg-white/15 backdrop-blur-sm p-[0.75em] text-[0.83em] max-w-[82%]">
        <div className="text-[1.2em] font-semibold">{name}</div>
        {role && <div className="opacity-85">{role}</div>}
        <Lines items={contacts} className="mt-[0.2em]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#EFF6FF] text-[#1E2A78] p-[1.4em] flex flex-col justify-center relative overflow-hidden">
      <div className="absolute -top-[2em] -right-[2em] h-[6em] w-[6em] rounded-full bg-[#3B82F6]/20" />
      {hasLogo && <Logo logo={logo} cls="relative h-[2.4em] w-[2.4em] mb-[0.4em]" />}
      <div
        className={`relative font-extrabold tracking-tight ${
          hasLogo ? "text-[1.4em]" : "text-[1.9em]"
        }`}
      >
        {biz}
      </div>
      <Tagline text={tag} className="relative text-[0.82em] text-[#2563EB]" />
      <Lines items={addr} className="relative mt-[0.4em] text-[0.85em] text-[#1E2A78]/75" />
    </div>
  );

/* ===================== BC07 — Executive · stone & charcoal ===================== */
const ExecutiveRule = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-[#F5F4F1] text-[#1F2124] p-[1.3em] flex flex-col justify-between">
      <div className="flex items-center justify-between gap-[0.6em]">
        {hasLogo ? (
          <Logo logo={logo} cls="h-[2.4em] w-[2.4em]" />
        ) : (
          <span className="grid place-items-center h-[2.4em] w-[2.4em] rounded-full border border-[#1F2124] text-[0.95em] font-bold text-[#1F2124]">
            {monogram(biz)}
          </span>
        )}
        <Tagline
          text={tag}
          className="font-mono text-[0.72em] uppercase tracking-[0.22em] text-[#8A8D93] text-right max-w-[62%]"
        />
      </div>
      <div>
        <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.4em]" : "text-[1.7em]"}`}>
          {biz}
        </div>
        <div className="my-[0.35em] h-px w-full bg-[#C9C7C1]" />
        <div className="flex items-end justify-between gap-[0.6em]">
          <div className="min-w-0">
            <div className="text-[1em] font-semibold">{name}</div>
            {role && <div className="text-[0.8em] text-[#6C7075]">{role}</div>}
          </div>
          <Lines items={contacts} className="font-mono text-[0.75em] text-right text-[#54585D]" />
        </div>
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#22252A] text-[#F5F4F1] p-[1.4em] flex flex-col justify-center">
      <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.3em]" : "text-[1.7em]"}`}>
        {biz}
      </div>
      <Tagline text={tag} className="font-mono text-[0.75em] uppercase tracking-[0.18em] text-[#B9BCC2]" />
      <div className="my-[0.35em] h-px w-[3em] bg-[#B98A2E]" />
      <Lines items={addr} className="font-mono text-[0.78em] opacity-80" />
    </div>
  );

/* ===================== BC08 — Clean · white & royal blue ===================== */
const ClassicCenter = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-white p-[0.6em]">
      <div className="h-full w-full border border-[#2447A8]/35 flex flex-col items-center justify-center text-center px-[1em] text-[#151A22]">
        {hasLogo && <Logo logo={logo} cls="h-[2.3em] w-[2.3em] mb-[0.4em]" />}
        <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.3em]" : "text-[1.75em]"}`}>
          {biz}
        </div>
        <Tagline text={tag} className="mt-[0.12em] max-w-full text-[0.8em] text-[#2447A8]" />
        <span className="my-[0.32em] h-[0.12em] w-[1.8em] bg-[#2447A8]" />
        <div className="text-[1em] font-semibold">{name}</div>
        {role && <div className="text-[0.82em] text-[#666E7C]">{role}</div>}
        <Lines items={contacts} className="mt-[0.28em] text-[0.82em] text-[#4C5665]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#F3F5FA] p-[0.6em]">
      <div className="h-full w-full border border-[#2447A8]/35 flex flex-col items-center justify-center text-center px-[1em] text-[#151A22]">
        <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.25em]" : "text-[1.7em]"}`}>
          {biz}
        </div>
        <Tagline text={tag} className="text-[0.8em] text-[#2447A8]" />
        <Lines items={addr} className="mt-[0.3em] text-[0.82em] text-[#4C5665]" />
      </div>
    </div>
  );

/* ===================== BC09 — Bold · yellow & black ===================== */
const Bold = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-[#F5C518] text-[#111111] flex flex-col justify-between">
      <div className="px-[1em] pt-[0.85em] flex items-start justify-between gap-[0.5em]">
        <div className="min-w-0">
          <div
            className={`font-extrabold uppercase leading-[0.95] tracking-tight ${
              hasLogo ? "text-[1.7em]" : "text-[2.2em]"
            }`}
          >
            {biz}
          </div>
          <Tagline
            text={tag}
            className="mt-[0.2em] text-[0.8em] font-semibold uppercase tracking-[0.14em]"
          />
        </div>
        {hasLogo && <Logo logo={logo} cls="h-[2.2em] w-[2.2em] shrink-0" />}
      </div>
      <div className="bg-[#111111] text-[#F5C518] px-[1em] py-[0.7em] text-[0.83em]">
        <div className="text-[1.15em] font-bold uppercase tracking-[0.05em] truncate">
          {name}
          {role ? ` · ${role}` : ""}
        </div>
        <Lines items={contacts} className="mt-[0.15em]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#111111] text-[#F5C518] p-[1.3em] flex flex-col justify-center">
      <div
        className={`font-extrabold uppercase leading-[0.95] ${
          hasLogo ? "text-[1.6em]" : "text-[2.05em]"
        }`}
      >
        {biz}
      </div>
      <Tagline text={tag} className="mt-[0.2em] text-[0.8em] uppercase tracking-[0.14em] text-white/70" />
      <Lines items={addr} className="mt-[0.4em] text-[0.84em] text-[#F5C518]/85" />
    </div>
  );

/* ===================== BC10 — Classic · deep navy band ===================== */
const VerticalFlow = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-white text-[#151A22] flex">
      <div className="w-[20%] bg-gradient-to-b from-[#17325C] to-[#0C1B33] text-white grid place-items-center">
        <span
          className="whitespace-nowrap font-semibold uppercase tracking-[0.28em] text-[0.85em]"
          style={{ transform: "rotate(-90deg)" }}
        >
          {biz.slice(0, 20)}
        </span>
      </div>
      <div className="flex-1 px-[1.2em] flex flex-col justify-center min-w-0">
        {hasLogo ? (
          <Logo logo={logo} cls="h-[2.2em] w-[2.2em] mb-[0.35em]" />
        ) : (
          <div className="font-bold tracking-tight text-[1.6em] leading-none">{biz}</div>
        )}
        <Tagline text={tag} className="mt-[0.15em] text-[0.82em] text-[#17325C]" />
        <div className="mt-[0.25em] text-[1.05em] font-semibold">{name}</div>
        {role && <div className="text-[0.82em] text-[#666E7C]">{role}</div>}
        <Lines items={contacts} className="mt-[0.28em] text-[0.82em] text-[#4C5665]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-gradient-to-br from-[#17325C] to-[#0A1526] text-white p-[1.4em] flex flex-col justify-center">
      <div className={`font-bold tracking-tight ${hasLogo ? "text-[1.3em]" : "text-[1.75em]"}`}>
        {biz}
      </div>
      <Tagline text={tag} className="text-[0.82em] text-[#C6D4EA]" />
      <Lines items={addr} className="mt-[0.4em] text-[0.84em] text-[#C6D4EA]/85" />
    </div>
  );

/* ===================== BC11 — Luxury · black & muted gold ===================== */
const Luxury = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-[#111010] text-[#F3EADA] p-[0.55em]">
      <div className="h-full w-full border border-[#B08D4F]/60 px-[1.2em] flex flex-col justify-center">
        <div className="flex items-center gap-[0.6em] min-w-0">
          {hasLogo ? (
            <Logo logo={logo} invert cls="h-[2.2em] w-[2.2em] shrink-0" />
          ) : (
            <span className="grid place-items-center h-[2.2em] w-[2.2em] shrink-0 rounded-full border border-[#B08D4F] text-[0.9em] font-semibold text-[#B08D4F]">
              {monogram(biz)}
            </span>
          )}
          <div className="min-w-0">
            <div
              className={`font-serif uppercase tracking-[0.16em] truncate ${
                hasLogo ? "text-[1.2em]" : "text-[1.45em]"
              }`}
            >
              {biz}
            </div>
            <Tagline text={tag} className="text-[0.76em] uppercase tracking-[0.2em] text-[#B08D4F]" />
          </div>
        </div>
        <div className="my-[0.5em] h-px w-full bg-gradient-to-r from-[#B08D4F] to-transparent" />
        <div className="text-[1.02em] font-semibold">{name}</div>
        {role && (
          <div className="text-[0.78em] uppercase tracking-[0.22em] text-[#B08D4F]">{role}</div>
        )}
        <Lines items={contacts} className="mt-[0.28em] text-[0.8em] text-[#F3EADA]/75" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#F3EADA] text-[#111010] p-[0.55em]">
      <div className="h-full w-full border border-[#111010]/25 flex flex-col items-center justify-center text-center px-[1em]">
        <div
          className={`font-serif uppercase tracking-[0.2em] ${
            hasLogo ? "text-[1.2em]" : "text-[1.5em]"
          }`}
        >
          {biz}
        </div>
        <Tagline text={tag} className="text-[0.76em] uppercase tracking-[0.2em] text-[#8A6A28]" />
        <span className="my-[0.35em] h-px w-[3em] bg-[#B08D4F]" />
        <Lines items={addr} className="text-[0.8em] text-[#111010]/75" />
      </div>
    </div>
  );

/* ===================== BC12 — Contemporary · teal & slate ===================== */
const Contemporary = ({ name, biz, role, tag, addr, contacts, hasLogo, logo, side }) =>
  side === "front" ? (
    <div className="h-full w-full bg-white text-[#141C1F] relative overflow-hidden flex flex-col justify-end p-[1.3em]">
      <div className="absolute -top-[4.5em] -right-[3.5em] h-[9em] w-[9em] rounded-full bg-gradient-to-br from-[#0E7490] to-[#134E4A]" />
      <div className="absolute top-[0.9em] right-[1.1em] text-right text-white">
        {hasLogo ? (
          <Logo logo={logo} invert cls="h-[2.1em] w-[2.1em] ml-auto" />
        ) : (
          <span className="text-[1.15em] font-extrabold tracking-[0.08em]">{monogram(biz)}</span>
        )}
      </div>
      <div className="relative min-w-0">
        <div className={`font-extrabold tracking-tight ${hasLogo ? "text-[1.5em]" : "text-[1.8em]"}`}>
          {biz}
        </div>
        <Tagline text={tag} className="text-[0.82em] text-[#0E7490]" />
        <div className="mt-[0.25em] flex items-center gap-[0.35em]">
          <span className="h-[0.14em] w-[1.4em] bg-[#0E7490]" />
          <span className="h-[0.14em] w-[0.7em] bg-[#D97706]" />
        </div>
        <div className="mt-[0.3em] text-[1.02em] font-semibold">{name}</div>
        {role && <div className="text-[0.82em] text-[#5D6B70]">{role}</div>}
        <Lines items={contacts} className="mt-[0.26em] text-[0.82em] text-[#485559]" />
      </div>
    </div>
  ) : (
    <div className="h-full w-full bg-[#0F2E33] text-white relative overflow-hidden p-[1.4em] flex flex-col justify-center">
      <div className="absolute -bottom-[4em] -left-[3em] h-[8em] w-[8em] rounded-full bg-[#0E7490]/35" />
      <div className={`relative font-extrabold tracking-tight ${hasLogo ? "text-[1.35em]" : "text-[1.8em]"}`}>
        {biz}
      </div>
      <Tagline text={tag} className="relative text-[0.82em] text-[#7DD3D8]" />
      <Lines items={addr} className="relative mt-[0.4em] text-[0.84em] text-white/80" />
    </div>
  );

const RENDERERS = {
  "minimalist-edge": Minimal,
  "corporate-split": Corporate,
  "modern-dark": ModernDark,
  "elegant-serif": Elegant,
  "two-tone": TwoTone,
  "creative-gradient": Creative,
  "executive-rule": ExecutiveRule,
  "classic-center": ClassicCenter,
  "bold-brutalist": Bold,
  "vertical-flow": VerticalFlow,
  "luxury-gold": Luxury,
  "contemporary-arc": Contemporary,
};
